/**
 * Eén notitie als rustige pagina (Notion als lat, Joris 25 sep 2026):
 * titel groot bovenaan, eigenschappen (Project · Account · Meeting) als stille rijen,
 * daaronder het schrijfvlak. Opslaan gaat vanzelf (debounced); geen Opslaan-knop.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Divider, IconButton, InputBase, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import MailNotitieTekst, { isMailNotitie } from '../../ui-components/MailNotitieTekst';
import NotitieSchrijver from './NotitieSchrijver';
import { EigenschapRij, KiesPopover, MeetingKoppelDialog, NaarProjectKnop, INKT, STIL, LIJN } from './NotitieOnderdelen';
import {
  SJABLONEN, korteDatum, meetingBlok, meetingUitTekst, naarOpslag, plekVanNotitie, plekVelden, splitsTitel, verplaatsVelden, vindProject, voegBlokToe,
} from './notitieLogica';

const AUTOSAVE_MS = 900;
const plekSleutel = (p) => JSON.stringify(plekVelden(p));

async function stuur(url, method, body) {
  const r = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || d.error || 'Opslaan mislukt');
  return d;
}

export default function NotitiePagina({
  notitie, startPlek, boom, boomIndex, mijnNaam, onTerug, onGewijzigd, onAangemaakt, onVerwijderd,
}) {
  const navigate = useNavigate();
  const start = useMemo(() => splitsTitel(notitie?.onderwerp, notitie?.notes), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [titel, setTitel] = useState(start.titel);
  const [tekst, setTekst] = useState(start.tekst);
  const [plek, setPlek] = useState(() => (notitie ? plekVanNotitie(notitie, boomIndex) : (startPlek || { type: 'los' })));
  const [leesModus, setLeesModus] = useState(() => isMailNotitie(notitie?.notes));
  const [status, setStatus] = useState('');
  const [menuAnker, setMenuAnker] = useState(null);
  const [kiesProject, setKiesProject] = useState(null);
  const [kiesAccount, setKiesAccount] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const idRef = useRef(notitie?.id || null);
  const titelRef = useRef(titel); titelRef.current = titel;
  const tekstRef = useRef(tekst); tekstRef.current = tekst;
  const plekRef = useRef(plek); plekRef.current = plek;
  // Wat er werkelijk in de database staat: inhoud en plek (Codex 25 sep, punt 4 en 5).
  const laatstRef = useRef(JSON.stringify(naarOpslag(start.titel, start.tekst)));
  // Laatst door de server bevestigde plek (object); basis voor 'vorige plek' bij ongedaan maken.
  const bevestigdePlekRef = useRef(notitie ? plekVanNotitie(notitie, boomIndex) : null);
  const bezigRef = useRef(false);
  const nogEensRef = useRef(false);
  const titelVeldRef = useRef(null);
  const schrijverFocus = useRef(null);

  // Nieuw: cursor in de titel.
  useEffect(() => { if (!notitie) setTimeout(() => titelVeldRef.current?.focus(), 30); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Verplaatsingen en hun ongedaan-makingen lopen achter elkaar (één keten), zodat de
  // database eindigt op wat het laatst gevraagd is; alleen de nieuwste mag de UI zetten —
  // een late response van een oudere stap wordt genegeerd (Codex 25 sep, P2).
  const plekKetenRef = useRef(Promise.resolve());
  const plekNrRef = useRef(0);
  const zetPlekInDb = useCallback((doel) => {
    const nr = plekNrRef.current + 1;
    plekNrRef.current = nr;
    const stap = plekKetenRef.current.catch(() => {}).then(async () => {
      // "Vorige plek" = wat de server bevestigd heeft op het moment dat deze stap begint —
      // niet de optimistische UI-plek, die door een geweigerde undo al 'los' kan zijn
      // terwijl de notitie nog op Full Frame staat (Codex 26 sep, P2).
      const vorige = bevestigdePlekRef.current;
      await stuur(`${BACKEND_URL}/Api/project-notes/${idRef.current}`, 'PATCH', verplaatsVelden(doel));
      bevestigdePlekRef.current = doel;
      return { actueel: nr === plekNrRef.current, vorige };
    });
    plekKetenRef.current = stap;
    return { stap, nr };
  }, []);

  const bewaar = useCallback(async () => {
    const t = titelRef.current; const x = tekstRef.current;
    if (!t.trim() && !x.trim()) return;
    const payload = naarOpslag(t, x);
    const sleutel = JSON.stringify(payload);
    if (sleutel === laatstRef.current && idRef.current) return;
    if (bezigRef.current) { nogEensRef.current = true; return; }
    bezigRef.current = true;
    setStatus('Bewaren…');
    let gelukt = false;
    try {
      if (idRef.current) {
        await stuur(`${BACKEND_URL}/Api/project-notes/${idRef.current}`, 'PATCH', payload);
        laatstRef.current = sleutel;
      } else {
        const plekBijAanmaak = plekRef.current;
        const d = await stuur(`${BACKEND_URL}/Api/project-notes`, 'POST', { ...payload, source: 'Manual', ...plekVelden(plekBijAanmaak) });
        idRef.current = d?.record?.id || d?.id || null;
        laatstRef.current = sleutel;
        bevestigdePlekRef.current = plekBijAanmaak;
        if (idRef.current) onAangemaakt?.(idRef.current);
        // Project/account gekozen terwijl de eerste opslag onderweg was: alsnog verplaatsen.
        const gewenst = plekRef.current;
        if (idRef.current && plekSleutel(gewenst) !== plekSleutel(bevestigdePlekRef.current)) {
          await zetPlekInDb(gewenst).stap;
        }
      }
      gelukt = true;
      setStatus('Opgeslagen');
      onGewijzigd?.({ id: idRef.current, onderwerp: payload.title, notes: payload.notes, ...plekNaarVelden(plekRef.current) });
    } catch (e) {
      setStatus('Niet opgeslagen');
      toast.error(`Notitie niet opgeslagen: ${e.message}`);
    } finally {
      bezigRef.current = false;
      // Wat nu in de editor staat kan afwijken van wat net is weggeschreven (bijv. terug-getypt
      // naar de vorige versie terwijl de save liep): dat verschil alsnog wegschrijven.
      const nu = JSON.stringify(naarOpslag(titelRef.current, tekstRef.current));
      if (nogEensRef.current || (gelukt && nu !== laatstRef.current)) { nogEensRef.current = false; bewaar(); }
    }
  }, [onAangemaakt, onGewijzigd, zetPlekInDb]);
  const bewaarRef = useRef(bewaar); bewaarRef.current = bewaar;

  useEffect(() => {
    const sleutel = JSON.stringify(naarOpslag(titel, tekst));
    if (sleutel === laatstRef.current) return undefined;
    setStatus('');
    const timer = setTimeout(() => bewaarRef.current(), AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [titel, tekst]);

  // Weg van de pagina (terug, andere notitie): wat er nog openstaat meteen wegschrijven.
  useEffect(() => () => { bewaarRef.current(); }, []);
  useEffect(() => {
    const h = (e) => { if (JSON.stringify(naarOpslag(titelRef.current, tekstRef.current)) !== laatstRef.current && (titelRef.current.trim() || tekstRef.current.trim())) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  // ── Plek (project / account) ──
  const zetPlekInUi = (p) => { setPlek(p); plekRef.current = p; };

  const verplaats = async (nieuw) => {
    zetPlekInUi(nieuw);
    // Nog niet bewaard: gaat mee bij de eerste opslag (of, als die al onderweg is,
    // verplaatst bewaar() hem direct na ontvangst van het id).
    if (!idRef.current) return;
    const { stap, nr } = zetPlekInDb(nieuw);
    try {
      const { actueel, vorige: oud } = await stap;
      if (!actueel) return; // intussen alweer verplaatst: die stap bepaalt UI en undo
      onGewijzigd?.({ id: idRef.current, ...plekNaarVelden(nieuw) });
      if (!oud) return;
      // Ook los → plek. Terug naar los mag alleen de auteur; voor een ander weigert de
      // engine (403) en toont het ongedaan maken die melding.
      biedOngedaanAan('Notitie verplaatst', async () => {
        const terugVan = plekRef.current;
        zetPlekInUi(oud);
        const undo = zetPlekInDb(oud);
        try {
          if ((await undo.stap).actueel) onGewijzigd?.({ id: idRef.current, ...plekNaarVelden(oud) });
        } catch (e) {
          if (undo.nr === plekNrRef.current) zetPlekInUi(bevestigdePlekRef.current || terugVan);
          throw e;
        }
      });
    } catch (e) {
      if (nr === plekNrRef.current && bevestigdePlekRef.current) zetPlekInUi(bevestigdePlekRef.current);
      toast.error(e.message);
    }
  };

  const projectOpties = useMemo(() => [...(boomIndex?.projecten?.values() || [])]
    .sort((a, b) => String(b.nummer).localeCompare(String(a.nummer)))
    .map((p) => ({ key: p.id, label: [p.nummer, p.naam].filter(Boolean).join(' · '), sub: p.account, waarde: { type: 'project', projectId: p.id, account: p.account } })), [boomIndex]);
  const accountOpties = useMemo(() => {
    const uit = [{ key: 'ff', label: 'Full Frame', sub: 'intern', waarde: { type: 'fullframe' } }];
    (boom || []).forEach((a) => {
      uit.push({ key: `a:${a.account}`, label: a.account, sub: 'Algemeen', waarde: { type: 'account', account: a.account, folder: '' } });
      (a.mappen || []).forEach((m) => uit.push({ key: `a:${a.account}:${m}`, label: a.account, sub: `map ${m}`, waarde: { type: 'account', account: a.account, folder: m } }));
    });
    return uit;
  }, [boom]);

  const project = plek.type === 'project' ? vindProject(boomIndex, plek.projectId) : null;
  const projectLabel = plek.type === 'project'
    ? ([project?.nummer || notitie?.projectnummer, project?.naam || notitie?.projectnaam].filter(Boolean).join(' · ') || 'Project')
    : '';
  const accountLabel = plek.type === 'fullframe' ? 'Full Frame'
    : plek.type === 'account' ? `${plek.account}${plek.folder ? ` · ${plek.folder}` : ''}`
      : plek.type === 'project' ? (project?.account || plek.account || '') : '';

  // ── Meeting, sjablonen ──
  const meeting = meetingUitTekst(tekst);
  const kiesMeeting = async (m) => {
    setMeetingOpen(false);
    setLeesModus(false);
    setTekst((t) => voegBlokToe(t, meetingBlok(m)));
    if (plek.type === 'project' && plek.projectId && !m.gekoppeld) {
      try {
        await stuur(`${BACKEND_URL}/Api/project-notes/meetings/koppel`, 'POST', { projectId: plek.projectId, meetingId: m.id });
        toast.success('Meeting ook aan het project gekoppeld');
      } catch { /* koppeling is een extraatje; de tekst staat er */ }
    }
  };
  const kiesSjabloon = (sj) => {
    setMenuAnker(null);
    setLeesModus(false);
    const klant = plek.type === 'account' ? plek.account : plek.type === 'project' ? (project?.account || '') : '';
    setTekst((t) => voegBlokToe(t, sj.maak({ klant, pl: mijnNaam })));
  };
  const slashActie = (key, editor) => {
    if (key === 'meeting') { setMeetingOpen(true); return; }
    if (key.startsWith('sjabloon:')) {
      const sj = SJABLONEN.find((s) => `sjabloon:${s.key}` === key);
      const klant = plek.type === 'account' ? plek.account : plek.type === 'project' ? (project?.account || '') : '';
      if (sj) editor.chain().focus().insertContent(sj.maak({ klant, pl: mijnNaam })).run();
    }
  };

  const verwijder = async () => {
    setMenuAnker(null);
    if (!idRef.current) { onTerug?.(); return; }
    if (!(await bevestig({ titel: 'Notitie verwijderen?', tekst: `"${titel || 'Naamloos'}" wordt verwijderd. Dit kan niet ongedaan worden gemaakt.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes/${idRef.current}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Verwijderen mislukt');
      laatstRef.current = JSON.stringify(naarOpslag(titelRef.current, tekstRef.current)); // niet meer wegschrijven
      toast.success('Notitie verwijderd');
      onVerwijderd?.(idRef.current);
    } catch (e) { toast.error(e.message); }
  };

  const wanneer = notitie ? [notitie.door, korteDatum(notitie.datum || notitie.at)].filter(Boolean).join(' · ') : '';

  return (
    <Box>
      {/* Balk: terug · status · ⋯ */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 3, md: 5 } }}>
        <ButtonBase onClick={onTerug} sx={{ gap: 0.5, px: 0.75, py: 0.4, ml: -0.75, borderRadius: 1, color: STIL, '&:hover': { bgcolor: '#f3f4f6' } }}>
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 13.5 }}>Notities</Typography>
        </ButtonBase>
        <Box sx={{ flex: 1 }} />
        <Typography aria-live="polite" sx={{ fontSize: 12.5, color: status === 'Niet opgeslagen' ? '#b91c1c' : STIL }}>{status}</Typography>
        <IconButton size="small" aria-label="Meer acties" onClick={(e) => setMenuAnker(e.currentTarget)} sx={{ color: STIL }}>
          <MoreHorizIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={menuAnker} open={!!menuAnker} onClose={() => setMenuAnker(null)}
          PaperProps={{ elevation: 0, sx: { border: `1px solid ${LIJN}`, boxShadow: '0 8px 24px rgba(15,23,42,0.10)', minWidth: 240 } }}>
          <ListSubheader sx={{ fontSize: 11.5, lineHeight: '28px', color: STIL }}>Sjabloon invoegen</ListSubheader>
          {SJABLONEN.map((sj) => <MenuItem key={sj.key} onClick={() => kiesSjabloon(sj)} sx={{ fontSize: 13.5 }}>{sj.label}</MenuItem>)}
          <Divider />
          <MenuItem onClick={() => { setMenuAnker(null); setMeetingOpen(true); }} sx={{ fontSize: 13.5 }}>Meeting koppelen</MenuItem>
          <MenuItem onClick={verwijder} sx={{ fontSize: 13.5, color: '#b91c1c' }}>Verwijderen</MenuItem>
        </Menu>
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <InputBase inputRef={titelVeldRef} fullWidth multiline value={titel} placeholder="Naamloos"
          onChange={(e) => setTitel(e.target.value.replace(/\n/g, ''))}
          onKeyDown={(e) => { if (e.key === 'Enter' || (e.key === 'ArrowDown' && !e.shiftKey)) { e.preventDefault(); setLeesModus(false); setTimeout(() => schrijverFocus.current?.focusBegin(), 0); } }}
          inputProps={{ 'aria-label': 'Titel' }}
          sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 700, color: INKT, lineHeight: 1.2, mb: 2,
            '& textarea::placeholder': { color: '#c4cad3', opacity: 1 } }} />

        <Box sx={{ mb: 2.5 }}>
          <EigenschapRij label="Project" waarde={projectLabel} onClick={(e) => setKiesProject(e.currentTarget)}
            extra={plek.type === 'project' && plek.projectId ? (
              <NaarProjectKnop projectId={plek.projectId} navigate={navigate} />
            ) : null} />
          <EigenschapRij label="Account" waarde={accountLabel} onClick={(e) => setKiesAccount(e.currentTarget)} />
          <EigenschapRij label="Meeting" waarde={meeting?.titel} onClick={() => setMeetingOpen(true)}
            extra={meeting ? (
              <ButtonBase onClick={() => navigate(`/MeetingDashboard/${meeting.id}`)}
                sx={{ ml: 1, px: 0.5, borderRadius: 1, fontSize: 12.5, color: STIL, '&:hover': { color: INKT } }}>
                open ↗
              </ButtonBase>
            ) : null} />
          {wanneer && <EigenschapRij label="Geschreven" waarde={wanneer} />}
        </Box>
        <Divider sx={{ borderColor: LIJN, mb: 2.5 }} />

        {leesModus ? (
          <Box>
            <MailNotitieTekst tekst={tekst} onderwerp={titel} />
            <ButtonBase onClick={() => setLeesModus(false)} sx={{ mt: 1.5, fontSize: 12.5, color: STIL, '&:hover': { color: INKT } }}>
              Tekst bewerken
            </ButtonBase>
          </Box>
        ) : (
          <NotitieSchrijver value={tekst} onChange={setTekst} onSlashActie={slashActie} focusRef={schrijverFocus} />
        )}
      </Box>

      <KiesPopover anchorEl={kiesProject} onClose={() => setKiesProject(null)} opties={projectOpties}
        placeholder="Zoek project op nummer of naam…"
        onKies={(o) => { setKiesProject(null); verplaats(o.waarde); }} />
      <KiesPopover anchorEl={kiesAccount} onClose={() => setKiesAccount(null)} opties={accountOpties}
        placeholder="Zoek account of map…"
        onKies={(o) => { setKiesAccount(null); verplaats(o.waarde); }} />
      <MeetingKoppelDialog open={meetingOpen} onClose={() => setMeetingOpen(false)}
        projectId={plek.type === 'project' ? plek.projectId : ''} onKies={kiesMeeting} />
    </Box>
  );
}

/** Plek terug naar de velden die de lijst gebruikt (projectId / klant / folder). */
function plekNaarVelden(plek) {
  if (plek.type === 'project') return { projectId: plek.projectId, klant: null, folder: null };
  if (plek.type === 'fullframe') return { projectId: null, klant: 'Full Frame', folder: null };
  if (plek.type === 'account') return { projectId: null, klant: plek.account, folder: plek.folder || null };
  return { projectId: null, klant: null, folder: null };
}
